import React, { useState, useEffect, useRef, useCallback } from 'react';
import { clsx } from 'clsx';
import { apiFetch } from 'ts-core';
import { useApi } from 'react-ui';
import type { EventLocationData, AddressData } from './EventLocationForm';

interface Venue {
  id: string;
  name: string;
  street: string | null;
  zip_code: string | null;
  address_locality: string | null;
  region: string | null;
  country: string | null;
  full_address: string | null;
  coordinates: { lat: number; lon: number } | null;
}

interface VenueAutocompleteProps {
  value: string;
  onChange: (name: string) => void;
  onSelect: (location: EventLocationData) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/** Inline building icon matching PiBuildingLight from react-icons/pi */
function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 256 256"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M232,224H208V32h8a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16h8V224H24a8,8,0,0,0,0,16H232a8,8,0,0,0,0-16ZM64,32H192V224H160V184a8,8,0,0,0-8-8H104a8,8,0,0,0-8,8v40H64Zm80,192H112V192h32ZM88,64a8,8,0,0,1,8-8h16a8,8,0,0,1,0,16H96A8,8,0,0,1,88,64Zm48,0a8,8,0,0,1,8-8h16a8,8,0,0,1,0,16H144A8,8,0,0,1,136,64ZM88,104a8,8,0,0,1,8-8h16a8,8,0,0,1,0,16H96A8,8,0,0,1,88,104Zm48,0a8,8,0,0,1,8-8h16a8,8,0,0,1,0,16H144A8,8,0,0,1,136,104ZM88,144a8,8,0,0,1,8-8h16a8,8,0,0,1,0,16H96A8,8,0,0,1,88,144Zm48,0a8,8,0,0,1,8-8h16a8,8,0,0,1,0,16H144A8,8,0,0,1,136,144Z" />
    </svg>
  );
}

/**
 * Venue name autocomplete that queries the regions API for venue suggestions.
 * Dropdown styling matches the map SearchBar venue items.
 */
export const VenueAutocomplete: React.FC<VenueAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Venue name (optional)',
  className,
  disabled = false,
}) => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { apiEndpoints, apiKey } = useApi();

  const searchVenues = useCallback(
    async (query: string) => {
      if (query.length < 2 || !apiEndpoints?.regions || !apiKey) {
        setVenues([]);
        return;
      }

      setIsLoading(true);
      try {
        const res = await apiFetch(
          `${apiEndpoints.regions}?name=${encodeURIComponent(query)}`,
          { method: 'GET', apiKey }
        );
        const data = await res.json();
        setVenues((data.venues ?? []).slice(0, 5));
        setShowDropdown(true);
      } catch {
        setVenues([]);
      } finally {
        setIsLoading(false);
      }
    },
    [apiEndpoints, apiKey]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setSelectedIndex(-1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (newValue.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        searchVenues(newValue);
      }, 300);
    } else {
      setVenues([]);
      setShowDropdown(false);
    }
  };

  const handleSelectVenue = (venue: Venue) => {
    const addressComponents: AddressData = {
      street: venue.street ?? '',
      zip: venue.zip_code ?? '',
      locality: venue.address_locality ?? '',
      country: venue.country ?? '',
      region: venue.region ?? '',
    };

    const location: EventLocationData = {
      name: venue.name,
      address: venue.full_address ?? '',
      coordinates: venue.coordinates
        ? { lat: venue.coordinates.lat, lng: venue.coordinates.lon }
        : { lat: 0, lng: 0 },
      addressComponents,
    };

    onChange(venue.name);
    onSelect(location);
    setVenues([]);
    setShowDropdown(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || venues.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < venues.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : venues.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < venues.length) {
          handleSelectVenue(venues[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => venues.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className={clsx('input pr-10', className)}
          disabled={disabled}
          autoComplete="off"
        />

        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            <BuildingIcon className="w-5 h-5" />
          </div>
        )}
      </div>

      {showDropdown && venues.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto py-1"
        >
          {venues.map((venue, index) => {
            const isActive = index === selectedIndex;
            return (
              <button
                key={venue.id}
                type="button"
                onClick={() => handleSelectVenue(venue)}
                className={clsx(
                  'w-full text-left cursor-default select-none py-2 pr-4 transition-colors',
                  isActive ? 'bg-balloon text-white' : 'text-gray-900 hover:bg-balloon hover:text-white',
                  '[&:hover_.venue-icon]:text-white [&:hover_.venue-sub]:text-white/70'
                )}
              >
                <span className="flex flex-row items-center truncate">
                  <span className="mx-2 w-6 h-6">
                    <BuildingIcon
                      className={clsx(
                        'venue-icon w-6 h-6',
                        isActive ? 'text-white' : 'text-gray-500'
                      )}
                    />
                  </span>
                  <span className="flex flex-col">
                    <span>{venue.name}</span>
                    {venue.address_locality && (
                      <span
                        className={clsx(
                          'venue-sub text-xs',
                          isActive ? 'text-white/70' : 'text-gray-400'
                        )}
                      >
                        {venue.zip_code} {venue.address_locality}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
